<script setup lang="ts">
import { ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import AppButton from '@uiKit/elements/Button.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'

interface Props {
  modelValue: File | null
  promptText?: string
}

withDefaults(defineProps<Props>(), {
  promptText: 'Tap to upload statement',
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: File | null): void
}>()

const fileInputRef = ref<HTMLInputElement | null>(null)
const fileError = ref('')

const VALID_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg']
const BYTES_PER_KB = 1024
const BYTES_PER_MB = BYTES_PER_KB * BYTES_PER_KB
const MAX_SIZE_MB = 10
const MAX_SIZE_BYTES = MAX_SIZE_MB * BYTES_PER_MB
const UPLOAD_ERROR_MESSAGE = `Uploaded file is not supported. Please upload a PDF or JPEG within ${MAX_SIZE_MB}mb.`

function openFilePicker() {
  fileInputRef.value?.click()
}

function handleFileChange(event: Event) {
  const input = event.target as HTMLInputElement
  if (!input.files || input.files.length === 0) return

  const file = input.files[0]
  if (!file) return

  if (!VALID_TYPES.includes(file.type)) {
    fileError.value = UPLOAD_ERROR_MESSAGE
    input.value = ''
    return
  }
  if (file.size > MAX_SIZE_BYTES) {
    fileError.value = UPLOAD_ERROR_MESSAGE
    input.value = ''
    return
  }

  fileError.value = ''
  emit('update:modelValue', file)
}

function removeFile() {
  emit('update:modelValue', null)
  fileError.value = ''
  if (fileInputRef.value) fileInputRef.value.value = ''
}

function formatSize(bytes: number): string {
  const mb = bytes / BYTES_PER_MB
  return mb < 1 ? `${(bytes / BYTES_PER_KB).toFixed(1)} KB` : `${mb.toFixed(1)} MB`
}
</script>

<template>
  <!-- Empty state -->
  <Box v-if="!modelValue" class="upload-empty" @click="openFilePicker">
    <PiIcon name="file" class="upload-icon" />
    <Text type="subheading" class="upload-prompt">{{ promptText }}</Text>
    <Text type="caption">PDF or JPEG · Max 10 MB</Text>
  </Box>

  <!-- Filled state -->
  <Box v-else class="upload-filled">
    <AppButton
      class="remove-btn"
      icon="pi pi-times"
      color="secondary"
      size="small"
      :pt="{
        root: {
          style: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        },
        icon: { style: { fontSize: '10px' } },
      }"
      @click="removeFile"
    />
    <Box class="file-icon-wrap">
      <PiIcon name="file" class="file-icon" />
    </Box>
    <Text type="subheading">{{ modelValue.name }}</Text>
    <Text type="caption">{{ formatSize(modelValue.size) }} · Uploaded</Text>
    <hr class="fill-divider" />
    <Box class="success-row">
      <PiIcon name="check" class="success-check" />
      <Text type="body" class="success-text">File meets all requirements</Text>
    </Box>
  </Box>

  <Text v-if="fileError" type="caption" class="file-error">{{ fileError }}</Text>

  <input
    ref="fileInputRef"
    type="file"
    accept=".pdf,.jpg,.jpeg"
    class="hidden-input"
    @change="handleFileChange"
  />
</template>

<style scoped>
/* Empty state */
.upload-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2.5rem 2rem;
  border: 2px dashed var(--p-primary-300);
  border-radius: 12px;
  background: var(--p-primary-50);
  cursor: pointer;
  transition:
    border-color 0.2s,
    background 0.2s;
  min-height: 200px;
}

.upload-empty:hover {
  border-color: var(--p-primary-500);
  background: var(--p-primary-100, var(--p-primary-50));
}

.upload-icon {
  font-size: 2.5rem;
  color: var(--p-surface-700);
  margin-bottom: 0.25rem;
}

.upload-prompt {
  color: var(--p-primary-600) !important;
}

/* Filled state */
.upload-filled {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 1rem 1rem 1.5rem;
  border: 2px dashed var(--p-success-300);
  border-radius: 12px;
  background: var(--p-success-50);
  min-height: 200px;
  text-align: center;
}

.remove-btn {
  position: absolute !important;
  top: 0.75rem;
  right: 0.75rem;
  width: 20px !important;
  height: 20px !important;
  border-radius: 50% !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: var(--p-surface-800) !important;
  border-color: var(--p-surface-800) !important;
  color: var(--p-surface-0) !important;
  min-width: unset !important;
}

.remove-btn :deep(.p-button-icon) {
  margin: 0 !important;
  line-height: 1 !important;
  position: static !important;
}

.file-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  /* height: 56px; */
  border-radius: 12px;
  /* background: var(--p-success-100); */
  margin-bottom: 0.25rem;
}

.file-icon {
  font-size: 40px;
  color: var(--p-success-600);
}

.fill-divider {
  border: none;
  border-top: 1px solid var(--p-success-200);
  width: 100%;
  margin: 10px 20px;
}

.success-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.success-check {
  font-size: 0.85rem;
  color: var(--p-success-600);
}

.success-text {
  color: var(--p-success-700) !important;
}

.file-error {
  color: var(--p-red-500) !important;
  text-align: center;
}

.hidden-input {
  display: none;
}
</style>
